import React, { useState, useEffect, useRef } from 'react';
import { usePreviewPng, uploadShareCard, saveImageBlob } from '@/utils/shareCard';
import { X, Download, Share2, Link2, Check, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { shareViaNative } from '@/utils/nativeShare';
import { SHARE_THEMES, getShareTheme } from '@/components/shareThemes';

import { useTranslation } from 'react-i18next';

/**
 * ShareRecapModal — themed share-card modal for the Weekly Recap page.
 *
 * Mirrors ShareDYKModal/ShareReflectionModal layout (5-theme picker,
 * live preview, 4 action buttons). The preview is a light HTML mirror
 * of the server-rendered 1200×630 PIL card; the actual sharable PNG
 * is fetched FROM the server (so what unfurls in iMessage/WhatsApp is
 * byte-identical to what we show in-app, and we don't need html2canvas
 * for this card).
 */
function RecapShareCardPreview({ theme, topics, dateRange, headline }) {
  const {
    t
  } = useTranslation();

  return (
    <div
      style={{
        background: `linear-gradient(180deg, ${theme.bg} 0%, ${theme.border} 100%)`,
        padding: '32px 28px 24px',
        fontFamily: "'Helvetica Neue', Arial, sans-serif",
      }}
    >
      <div
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '6px 14px', borderRadius: 999,
          background: theme.tagBg, color: theme.accent,
          fontSize: 11, fontWeight: 700, letterSpacing: '0.12em',
          textTransform: 'uppercase', marginBottom: 12,
        }}
      >
        {t('generated.sharerecapmodal_weekly_recap_8e78')}
      </div>
      {dateRange && (
        <p style={{ color: theme.secondary, fontSize: 13, margin: '0 0 8px' }}>
          {dateRange}
        </p>
      )}
      <h2 style={{
        fontSize: 22, fontWeight: 700, lineHeight: 1.25,
        color: theme.topicColor, margin: '0 0 14px',
      }}>
        {headline}
      </h2>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
        {topics.slice(0, 10).map((t, i) => (
          <span key={i} style={{
            padding: '5px 12px', borderRadius: 999,
            background: theme.tagBg, color: theme.accent,
            fontSize: 12, fontWeight: 500,
          }}>
            {(t.label || '').slice(0, 32)}{(t.label || '').length > 32 ? '…' : ''}
          </span>
        ))}
      </div>
      <div style={{
        paddingTop: 10, borderTop: `1px solid ${theme.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <img
            src="/logo.png"
            alt={t('generated.sharerecapmodal_eili_3729')}
            style={{ width: 28, height: 28, objectFit: 'contain' }}
            crossOrigin="anonymous"
          />
          <span style={{
            fontFamily: "'Playwrite ES Deco', cursive",
            fontSize: 14, fontWeight: 700,
            color: theme.accent,
            marginTop: 3,
          }}>
            {t('generated.sharerecapmodal_eili_3729')}
          </span>
        </div>
        <span style={{ fontSize: 10, color: theme.secondary }}>
          askeili.com
        </span>
      </div>
    </div>
  );
}

export default function ShareRecapModal({ topics, weekStart, weekEnd, onClose }) {
  const {
    t
  } = useTranslation();

  const [selectedTheme, setSelectedTheme] = useState('classic');
  const [linkCopied, setLinkCopied] = useState(false);
  const [shareInfo, setShareInfo] = useState(null);
  const [creating, setCreating] = useState(true);
  const [busy, setBusy] = useState(false);

  const theme = getShareTheme(selectedTheme);
  const apiBase = `${window.location.origin}/api`;
  const headline = 'What I studied this week';
  const dateRange = weekStart && weekEnd ? `${weekStart} – ${weekEnd}` : '';

  // Mint the share record once on mount. Don't re-mint per theme.
  useEffect(() => {
    let alive = true;
    setCreating(true);
    axios
      .post(`${apiBase}/recap/share`,
        { topics, week_start: weekStart, week_end: weekEnd, theme: selectedTheme },
        { withCredentials: true, timeout: 8000 })
      .then((r) => { if (alive) setShareInfo(r.data); })
      .catch(() => { if (alive) toast.error(t('generated.sharerecapmodal_could_not_create_dd52')); })
      .finally(() => { if (alive) setCreating(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shareUrl = shareInfo
    ? `${window.location.origin}/api/og/share/${shareInfo.share_id}`
    : null;

  // The official image is a capture of the live preview, so they always match.
  const cardRef = useRef(null);
  const pngBlob = usePreviewPng(cardRef, selectedTheme, 1200);
  const syncCard = () => { if (shareInfo) uploadShareCard(shareInfo.share_id, pngBlob); };

  const handleShareImage = async () => {
    if (!pngBlob) return;
    setBusy(true);
    syncCard();
    try {
      const file = new File([pngBlob], `eili-recap-${Date.now()}.png`, { type: 'image/png' });
      const result = await shareViaNative({
        title: t('generated.sharerecapmodal_my_weekly_recap_0c73'),
        text: `My Weekly Recap — ${dateRange}`,
        url: shareUrl,
        file,
      });
      if (!result?.aborted && !result?.shared) {
        toast.info(t('generated.sharerecapmodal_use_save_or_e0db'));
      }
    } catch {
      toast.error(t('generated.sharerecapmodal_could_not_open_c66b'));
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = () => saveImageBlob(pngBlob, `eili-recap-${Date.now()}.png`);

  const handleCopyLink = async () => {
    if (!shareUrl) return;
    syncCard();
    try {
      await navigator.clipboard.writeText(shareUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      toast.error(t('generated.sharerecapmodal_could_not_copy_0729'));
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
      data-testid="share-recap-modal"
    >
      <div
        className="relative w-full max-w-lg flex flex-col rounded-2xl overflow-hidden"
        style={{
          background: 'var(--theme-surface, #1e293b)',
          border: '1px solid var(--glass-border, #334155)',
          maxHeight: 'min(90vh, calc(var(--visual-vh, 100vh) - 32px))',
        }}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b"
             style={{ borderColor: 'var(--glass-border, #334155)' }}>
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" style={{ color: 'var(--theme-accent)' }} />
            <h3 className="text-base font-semibold" style={{ color: 'var(--theme-text)' }}>
              {t('generated.sharerecapmodal_share_your_weekly_65cf')}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full transition hover:bg-white/10"
            data-testid="share-recap-close"
          >
            <X className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {/* Theme picker */}
          <div>
            <p className="text-xs font-medium uppercase tracking-wider mb-3"
               style={{ color: 'var(--theme-textSecondary)' }}>
              {t('generated.sharerecapmodal_style_3713')}
            </p>
            <div className="flex gap-2.5 flex-wrap" data-testid="share-recap-theme-picker">
              {SHARE_THEMES.map((t) => {
                const active = selectedTheme === t.key;
                return (
                  <button
                    key={t.key}
                    onClick={() => setSelectedTheme(t.key)}
                    className="flex flex-col items-center gap-1.5 transition-all"
                    data-testid={`share-recap-theme-${t.key}`}
                  >
                    <div
                      className="w-10 h-10 rounded-full relative overflow-hidden transition-all"
                      style={{
                        background: t.swatch[0],
                        border: active ? `2.5px solid ${t.swatch[1]}` : '2px solid transparent',
                        boxShadow: active ? `0 0 0 2px ${t.swatch[1]}40` : 'none',
                      }}
                    >
                      <div
                        className="absolute bottom-0 right-0 w-5 h-5 rounded-tl-full"
                        style={{ background: t.swatch[1] }}
                      />
                    </div>
                    <span className="text-[10px] font-medium"
                          style={{ color: active ? 'var(--theme-text)' : 'var(--theme-textSecondary)' }}>
                      {t.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Preview */}
          <div>
            <p className="text-xs font-medium uppercase tracking-wider mb-3"
               style={{ color: 'var(--theme-textSecondary)' }}>
              {t('generated.sharerecapmodal_preview_0972')}
            </p>
            <div className="rounded-xl overflow-hidden"
                 style={{ border: '1px solid var(--glass-border)' }}>
              <div ref={cardRef} data-testid="share-recap-preview">
                <RecapShareCardPreview
                  theme={theme}
                  topics={topics}
                  dateRange={dateRange}
                  headline={headline}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="px-5 py-4 border-t flex flex-wrap gap-2.5"
             style={{ borderColor: 'var(--glass-border, #334155)' }}>
          <Button
            onClick={handleShareImage}
            disabled={busy || !pngBlob || creating}
            className="flex-1 min-w-[120px] rounded-xl py-4 font-medium gap-2"
            style={{ background: theme.accent, color: '#fff' }}
            data-testid="share-recap-image-btn"
          >
            {!pngBlob ? (
              <>
                <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {t('generated.sharerecapmodal_preparing_2cf3')}
              </>
            ) : (
              <>
                <Share2 className="h-4 w-4" />
                {busy ? 'Sharing…' : 'Share Image'}
              </>
            )}
          </Button>
          <Button
            onClick={handleDownload}
            disabled={!pngBlob}
            variant="outline"
            className="rounded-xl py-4 font-medium gap-2"
            style={{ borderColor: 'var(--glass-border)', color: 'var(--theme-text)' }}
            data-testid="share-recap-download-btn"
          >
            <Download className="h-4 w-4" />
            {t('generated.sharerecapmodal_save_1c93')}
          </Button>
          <Button
            onClick={handleCopyLink}
            disabled={!shareUrl || !pngBlob}
            variant="outline"
            className="rounded-xl py-4 font-medium gap-2"
            style={{ borderColor: 'var(--glass-border)', color: 'var(--theme-text)' }}
            data-testid="share-recap-copy-link-btn"
          >
            {linkCopied ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}
            {linkCopied ? 'Copied' : 'Link'}
          </Button>
        </div>
      </div>
    </div>
  );
}
